import { faqsPl } from "@data/system-iot-landing/faq";
import {
  aboutUsCopyPl,
  contactFormPl,
  faqStructuredData,
  iotClusterLinks,
  iotKnowledgeBaseLinks,
  organizationStructuredData,
  serviceStructuredData,
} from "@data/iot-landing/shared";
import {
  Calendar,
  CheckCircle,
  Cloud,
  Code,
  Cpu,
  FileText,
  Globe,
  LayoutDashboard,
  Layers,
  LifeBuoy,
  PenTool,
  Rocket,
  Search,
  ShieldCheck,
  Smartphone,
} from "lucide-react";

import type { FAQCopy } from "@components/bluetooth-landing/FAQ";
import type { FooterCopy } from "@components/bluetooth-landing/Footer";
import type { HeroCopy } from "@components/bluetooth-landing/Hero";
import type { ProcessCopy } from "@components/bluetooth-landing/Process";
import type { ServicesCopy } from "@components/bluetooth-landing/Services";
import type { TargetAudienceCopy } from "@components/bluetooth-landing/TargetAudience";
import type { ValuePropositionCopy } from "@components/bluetooth-landing/ValueProposition";

export const title =
  "System IoT na zamówienie - urządzenie, aplikacja, backend i dashboard";

export const description =
  "System IoT na zamówienie dla firm — urządzenie, firmware, aplikacja mobilna, backend i dashboard w jednym zespole. Platforma IoT i rozwiązania IoT dla firm od NDA i PoC po produkcję i utrzymanie floty.";

export const structuredData = [
  organizationStructuredData(description),
  serviceStructuredData(
    "System IoT na zamówienie",
    description,
    "Custom IoT platform development",
  ),
  faqStructuredData(faqsPl),
];

export const heroCopy: HeroCopy = {
  header: "System IoT",
  headerTail: "na zamówienie",
  description:
    "Budujemy kompletne rozwiązania IoT dla firm — urządzenie z firmware, aplikację mobilną, backend i dashboard monitoringu. Jedna odpowiedzialność za cały produkt connected, od NDA i PoC na 2–3 miesiące po wdrożenie produkcyjne.",
  cta: {
    text: "Darmowa konsultacja",
    href: "#kontakt",
  },
  secondaryCta: {
    text: "Poznaj nasz proces",
    href: "#proces",
  },
  benefits: [
    { text: "Urządzenie + app + dashboard", Icon: CheckCircle },
    { text: "PoC pod NDA w 2–3 miesiące", Icon: CheckCircle },
    { text: "Integracja z hardware OEM", Icon: CheckCircle },
  ],
};

export const targetAudienceCopy: TargetAudienceCopy = {
  header: {
    h2: "Kim są nasi klienci?",
    h3: "Dla kogo jest system IoT na zamówienie?",
    p: "W leadach klienci prawie nigdy nie proszą o sam czujnik — chcą urządzenie z panelem, aplikacją i chmurą. Budujemy platformę IoT end-to-end, bo problemy najczęściej wychodzą na styku warstw. Czy to brzmi jak Twoja sytuacja?",
  },
  points: [
    {
      Icon: Rocket,
      title: "Startujesz z pomysłem na produkt IoT i szukasz partnera end-to-end",
      description:
        "Masz koncepcję urządzenia connected, ale nie chcesz koordynować osobno elektroniki, firmware, aplikacji i chmury — potrzebujesz jednego zespołu od PoC po MVP.",
    },
    {
      Icon: Globe,
      title: "Masz hardware OEM i potrzebujesz warstwy software",
      description:
        "Urządzenie pochodzi od producenta OEM/ODM — integrujemy firmware, aplikację mobilną i backend pod własną markę klienta, porządkując protokół komunikacji.",
    },
    {
      Icon: LayoutDashboard,
      title: "Chcesz dashboard IoT, nie sam czujnik bez widoku danych",
      description:
        "Sensory mają sens dopiero z panelem — wykresy, alerty, zarządzanie flotą i konfiguracja urządzeń. Budujemy dashboard razem z urządzeniem, nie jako osobny projekt.",
    },
    {
      Icon: ShieldCheck,
      title: "Zaczynasz od NDA i krótkiego PoC",
      description:
        "Chcesz zweryfikować technologię i łączność w wąskim, mierzalnym zakresie — typowy scenariusz to NDA, PoC na 2–3 miesiące i decyzja o dalszej inwestycji.",
    },
    {
      Icon: Layers,
      title: "Masz działający fragment i potrzebujesz domknięcia systemu",
      description:
        "Firmware działa, ale brakuje aplikacji, backendu albo panelu administracyjnego — dobudowujemy brakujące warstwy i integrujemy je z istniejącym kodem.",
    },
  ],
  cta: {
    h4: "Brzmi znajomo?",
    text: "Opowiedz nam o swoim produkcie IoT — nawet jeśli masz tylko wymagania biznesowe. Zaproponujemy zakres PoC lub pełnego systemu.",
    href: "#kontakt",
    buttonText: "Umów darmową konsultację",
  },
};

export const valuePropositionCopy: ValuePropositionCopy = {
  header: {
    h2: "Nasza specjalizacja",
    h3: "Dlaczego warto budować system IoT z nami?",
    p: "Rozwiązania IoT dla firm wymagają integracji wielu warstw — hardware, firmware, aplikacja, backend i dashboard. Jeden zespół i jedna odpowiedzialność eliminują problemy, które w leadach pojawiają się najczęściej: na styku urządzenia i oprogramowania.",
    imageAlt: "System IoT na zamówienie — urządzenie, aplikacja i dashboard",
  },
  values: [
    {
      Icon: Layers,
      title: "End-to-end: urządzenie, app, backend, dashboard",
      description:
        "Nie rozdzielamy produktu na trzy osobne zlecenia — budujemy platformę IoT jako spójny system, w którym każda warstwa jest zaprojektowana pod pozostałe.",
    },
    {
      Icon: Cpu,
      title: "Elektronika i firmware w tym samym zespole",
      description:
        "Projektujemy PCB, piszemy firmware i integrujemy z chmurą — bez przerzucania problemów między dostawcami hardware i software.",
    },
    {
      Icon: LayoutDashboard,
      title: "Dashboard IoT gotowy do użytku biznesowego",
      description:
        "Panel z wykresami, alertami, zarządzaniem flotą i konfiguracją urządzeń — nie demo-instancja, ale narzędzie dla operatorów i klientów końcowych.",
    },
    {
      Icon: Cloud,
      title: "Architektura chmurowa pod skalę floty",
      description:
        "AWS lub self-hosted w UE — projektujemy backend pod tysiące urządzeń, retencję danych, RODO i koszty operacyjne, nie pod jedną sztukę demo.",
    },
    {
      Icon: Rocket,
      title: "PoC pod NDA — szybka weryfikacja pomysłu",
      description:
        "Wąski, mierzalny zakres na 2–3 miesiące — zweryfikujesz technologię, łączność i UX zanim zainwestujesz w pełną platformę IoT.",
    },
  ],
};

export const servicesCopy: ServicesCopy = {
  header: {
    h2: "Usługi",
    h3: "System IoT na zamówienie — co budujemy",
    p: "Kompletna platforma IoT dla firm — od urządzenia po panel administracyjny. Zakres dopasowujemy do etapu: PoC, MVP albo produkt komercyjny z utrzymaniem floty.",
  },
  services: [
    {
      Icon: Search,
      title: "Analiza produktu i architektura systemu IoT",
      description:
        "Mapujemy wymagania biznesowe, dobór łączności, hardware i architekturę chmurową. Otrzymujesz plan systemu IoT z widełkami kosztów i harmonogramem.",
    },
    {
      Icon: Cpu,
      title: "Urządzenie IoT — elektronika i firmware",
      description:
        "Projektujemy PCB lub integrujemy hardware OEM, piszemy firmware z obsługą sensorów, łączności i OTA — urządzenie gotowe do pracy w flocie.",
    },
    {
      Icon: Smartphone,
      title: "Aplikacja mobilna (Flutter / natywna)",
      description:
        "Aplikacja towarzysząca urządzeniu — parowanie, konfiguracja, podgląd danych i powiadomienia. Flutter lub natywne iOS/Android, zależnie od wymagań.",
    },
    {
      Icon: Cloud,
      title: "Backend i API IoT",
      description:
        "Serwer odbierający dane z urządzeń, API REST/GraphQL, autoryzacja, zarządzanie flotą i integracja z systemami klienta (ERP, CRM).",
    },
    {
      Icon: LayoutDashboard,
      title: "Dashboard i panel administracyjny",
      description:
        "Webowy panel z wykresami, alertami, mapą urządzeń, historią danych i zarządzaniem użytkownikami — centrum operacyjne platformy IoT.",
    },
    {
      Icon: Globe,
      title: "Integracja z hardware OEM/ODM",
      description:
        "Przyjmujemy urządzenia od zewnętrznych producentów, porządkujemy protokół, piszemy firmware i budujemy warstwę software pod marką klienta.",
    },
    {
      Icon: ShieldCheck,
      title: "Bezpieczeństwo, RODO i certyfikacja",
      description:
        "Szyfrowanie komunikacji, zarządzanie certyfikatami, zgodność z RODO i przygotowanie dokumentacji pod certyfikację CE produktu IoT.",
    },
    {
      Icon: LifeBuoy,
      title: "Wdrożenie, utrzymanie i rozwój platformy",
      description:
        "Monitoring floty, aktualizacje firmware OTA, utrzymanie backendu i kolejne iteracje produktowe po premierze systemu IoT.",
    },
  ],
  cta: {
    h4: "Nie wiesz, od czego zacząć?",
    p: "Opisz nam swój produkt IoT — nawet jeśli masz tylko pomysł biznesowy. Zaproponujemy wąski PoC albo pełny zakres platformy.",
    button: {
      text: "Umów darmową konsultację",
      href: "#kontakt",
    },
  },
};

export const processCopy: ProcessCopy = {
  header: {
    h2: "Nasz Proces",
    h3: "Jak powstaje system IoT na zamówienie",
    p: "Od konsultacji i NDA po działającą platformę IoT z urządzeniem, aplikacją i dashboardem. Przejrzyste etapy i mierzalne kamienie milowe.",
  },
  stepNumber: "Krok",
  steps: [
    {
      Icon: Calendar,
      title: "Darmowe spotkanie online",
      duration: "w kilka godzin",
      description:
        "30 minut rozmowy z inżynierem. Opisujesz produkt IoT, cele biznesowe i ograniczenia — dostajesz pierwszą ocenę wykonalności i propozycję zakresu PoC.",
    },
    {
      Icon: FileText,
      title: "Warsztaty i plan architektury",
      duration: "w kilka dni",
      description:
        "Mapujemy warstwy systemu — urządzenie, firmware, app, backend, dashboard. Otrzymujesz diagram architektury, harmonogram i widełki budżetowe.",
    },
    {
      Icon: PenTool,
      title: "NDA i umowa na PoC lub MVP",
      duration: "ustalenie harmonogramu",
      description:
        "Podpisujemy NDA, ustalamy wąski, mierzalny zakres — typowo PoC na 2–3 miesiące z jasnymi kryteriami sukcesu.",
    },
    {
      Icon: Code,
      title: "Budowa kluczowych warstw równolegle",
      duration: "min. 6–12 tygodni",
      description:
        "Elektronika/firmware, backend i dashboard powstają równolegle — żeby jak najszybciej mieć działający fragment systemu IoT do testów.",
    },
    {
      Icon: Smartphone,
      title: "Integracja urządzenia z aplikacją i chmurą",
      duration: "2–4 tygodnie",
      description:
        "Spinamy warstwy w spójny produkt — dane płyną z urządzenia przez backend do dashboardu i aplikacji mobilnej.",
    },
    {
      Icon: Search,
      title: "Testy z użytkownikami i iteracje",
      duration: "2–4 tygodnie",
      description:
        "Testujemy system IoT w realnych warunkach z operatorami i klientami końcowymi. Wprowadzamy poprawki w UX, stabilności i wydajności.",
    },
    {
      Icon: Rocket,
      title: "Wdrożenie produkcyjne",
      duration: "min. 2–4 tygodnie",
      description:
        "Przygotowujemy infrastrukturę pod flotę, certyfikację CE, monitoring i dokumentację operacyjną platformy IoT.",
    },
    {
      Icon: CheckCircle,
      title: "Utrzymanie i rozwój produktu",
      duration: "na stałe",
      description:
        "Monitoring floty, OTA, utrzymanie backendu i kolejne iteracje funkcji — albo przekazujemy system z dokumentacją do Waszego zespołu.",
    },
  ],
  cta: {
    href: "#kontakt",
    text: "Zamów system IoT",
  },
};

export const aboutUsCopy = aboutUsCopyPl({
  imageAlt: "Zespół Blues Brackets — systemy IoT na zamówienie",
});

export const faqCopy: FAQCopy = {
  header: {
    h2: "FAQ",
    h3: "Najczęściej zadawane pytania o system IoT na zamówienie",
  },
  faqs: faqsPl,
};

export const footerCopy: FooterCopy = {
  header: {
    h2: "Budujesz platformę IoT?",
    p: "Umów się na darmową konsultację — opowiedz o urządzeniu, aplikacji i dashboardzie, a zaproponujemy zakres PoC lub pełnego systemu IoT.",
  },
  cta: {
    href: "#kontakt",
    text: "Umów darmową konsultację",
    p: "System IoT na zamówienie — urządzenie, firmware, aplikacja, backend i dashboard. Rozwiązania IoT dla firm od PoC po produkcję.",
    email: "hello@bluesbrackets.com",
    phone: "+48 535 462 678",
  },
  links: {
    h4: "Na skróty",
    links: [
      { href: "#uslugi", text: "Nasze usługi" },
      { href: "#proces", text: "Proces współpracy" },
      { href: "#faq", text: "FAQ" },
      { href: "/iot", text: "Systemy IoT" },
      ...iotClusterLinks.filter(
        l => l.href !== "/iot" && l.href !== "/system-iot-na-zamowienie",
      ),
    ],
  },
  knowledgeBase: {
    h4: "Baza wiedzy",
    links: [...iotKnowledgeBaseLinks],
  },
  copyright: "bluesBrackets. Wszelkie prawa zastrzeżone.",
  privacyPolicy: "Polityka prywatności",
  privacyPolicyHref: "/policy",
  contactForm: contactFormPl,
};
